import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Header, Label, Divider } from 'semantic-ui-react';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement
} from '@stripe/react-stripe-js';

import { useDispatch } from 'react-redux';
import { setToast } from '../../store/ToastManagement';

import {
  servicesAPI,
  useCurrentUser,
  useCustomer,
  useCustomerBillingAccount
} from '../../_services';
import './stripe.css';

export const CardDetailsModal = ({
  edit,
  buttonText = '',
  callback = null,
  reload = true,
  userId = 0,
  autoOpen = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const { customerId, currentUser } = useCurrentUser();
  const { customer } = useCustomer(customerId, { formatData: false });
  const account = useCustomerBillingAccount(customerId);

  const [errored, setErrored] = useState('');

  const dispatch = useDispatch();
  const displaySuccess = () =>
    dispatch(
      setToast({
        title: 'Success!',
        body: 'Card details successfully validated.'
      })
    );

  const CARD_ELEMENT_OPTIONS = {
    style: {
      base: {
        color: '#32325d',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#aab7c4'
        }
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a'
      }
    }
  };

  const CardSetupForm = () => {
    const stripe = useStripe();
    const elements = useElements();

    const handleSubmit = async () => {
      if (!stripe || !elements) return;

      // Set up Stripe payment intent
      const clientSecret = await servicesAPI
        .BillingServices('getAccountSecret', account.id)
        .then(
          result => {
            console.log(
              'TRJN_DBG: Success contacting Stripe card vault service'
            );
            setErrored('');
            return result;
          },
          error => {
            console.log(
              'TRJN_DBG: Problem contacting Stripe card vault service'
            );
            setErrored('Problem contacting card vault service');
            return null;
          }
        );

      if (!clientSecret) return;

      console.log('TRJN_DBG: Got Stripe client secret');

      const result = await stripe.confirmCardSetup(clientSecret.secret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
          billing_details: {
            name: customer.forename + ' ' + customer.surname
          }
        }
      });

      if (result.error) {
        console.log(
          `TRJN_DBG: Failed to confirm card setup with Stripe: ${result.error.message}`
        );
        setErrored(result.error.message);
      } else {
        console.log(`TRJN_DBG: Success confirming card setup with Stripe`);
        setErrored('');

        const values = {
          stripe_payment_method: result.setupIntent.payment_method
        };
        servicesAPI
          .BillingServices('setAccountPaymentMethod', account.id, values)
          .then(
            result => {
              console.log(`TRJN_DBG: Success saving card to billing service`);
              // Update cache
              localStorage.setItem('accountOnboarded', true);

              setErrored('Card saved successfully');
              setSuccess(true);

              if (callback) callback();
              if (reload) window.location.reload();
            },
            error => {
              console.log(`TRJN_DBG: Failed to save card to billing service`);
              setErrored('Failed to save card');
              setSuccess(false);
            }
          );
      }
    };

    return (
      <Form>
        <Header as="h2" textAlign="left">
          Card Details
        </Header>
        <p>
          By entering your card details you are consenting to store them in our
          third party payment service, Stripe. Payment for your use of the
          Services is made at the end of each charging session and will be
          automatically charged to your chosen card.
        </p>
        <Divider hidden />
        <label>Card number</label>
        <CardNumberElement options={CARD_ELEMENT_OPTIONS} />
        <br />
        <label>Expiry date</label>
        <CardExpiryElement options={CARD_ELEMENT_OPTIONS} />
        <br />
        <label>3-digit security code</label>
        <CardCvcElement options={CARD_ELEMENT_OPTIONS} />
        {errored && (
          <>
            <br />
            <Label basic color={'red'} className={'red'}>
              {errored}
            </Label>
          </>
        )}
        <Divider hidden />
        <Button
          color="olive"
          disabled={!stripe}
          onClick={event => {
            event.preventDefault();
            handleSubmit();
          }}
        >
          Save
        </Button>
        <Button onClick={cancel}>Cancel</Button>
      </Form>
    );
  };

  useEffect(() => {
    if (autoOpen) {
      setSuccess(false);
      setErrored('');
      setIsModalOpen(true);
    }
  }, [autoOpen]);

  useEffect(() => {
    if (isModalOpen && success) {
      setIsModalOpen(false);
      displaySuccess();
    }
  }, [isModalOpen, success, displaySuccess]);

  const open = event => {
    event.preventDefault();
    event.stopPropagation();
    setSuccess(false);
    setErrored('');
    setIsModalOpen(true);
  };

  const cancel = event => {
    event.preventDefault();
    event.stopPropagation();
    setSuccess(false);
    setErrored('');
    setIsModalOpen(false);
  };

  return (
    <React.Fragment>
      {!autoOpen ? (
        <Button
          color="olive"
          onClick={open}
          disabled={!edit || currentUser?.impersonating}
        >
          {buttonText}
        </Button>
      ) : (
        ''
      )}
      <Modal open={isModalOpen} size="small" style={{ padding: '20px' }}>
        <Modal.Content>
          <CardSetupForm />
        </Modal.Content>
      </Modal>
    </React.Fragment>
  );
};
